import React from "react";
import { Upload, Image, Button, Tooltip } from "antd";
import {
  DeleteOutlined,
  LeftOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  ALLOWED_GIF_ACCEPT,
  MAX_GIF_IMAGES,
  MIN_GIF_IMAGES,
  MIN_SOURCE_DIMENSION,
  MAX_SOURCE_DIMENSION,
  createGifImageId,
} from "./gifConstants";
import { getRemainingImageSlots, validateGifSourceFile } from "./validateGifImages";

const GifImageUploader = ({ value = [], onChange }) => {
  const images = Array.isArray(value) ? value : [];
  const remainingSlots = getRemainingImageSlots(images.length);

  const emit = (nextImages) => {
    onChange?.(nextImages);
  };

  const handleFiles = async (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;

    if (remainingSlots <= 0) {
      toast.error(`You can upload a maximum of ${MAX_GIF_IMAGES} images.`);
      return;
    }

    if (picked.length > remainingSlots) {
      toast.error(
        `You can add ${remainingSlots} more image${remainingSlots === 1 ? "" : "s"} (maximum ${MAX_GIF_IMAGES}).`
      );
    }

    const accepted = [];
    const filesToCheck = picked.slice(0, remainingSlots);

    for (const file of filesToCheck) {
      const result = await validateGifSourceFile(file);
      if (!result.ok) {
        toast.error(result.message);
        continue;
      }

      accepted.push({
        uid: createGifImageId(),
        file,
        url: URL.createObjectURL(file),
        name: file.name,
        width: result.width,
        height: result.height,
      });
    }

    if (accepted.length) {
      emit([...images, ...accepted]);
    }
  };

  const handleRemove = (uid) => {
    const target = images.find((img) => img.uid === uid);
    if (target?.url?.startsWith("blob:")) {
      URL.revokeObjectURL(target.url);
    }
    emit(images.filter((img) => img.uid !== uid));
  };

  const moveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= images.length) return;
    const next = [...images];
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    emit(next);
  };

  return (
    <div className="gif-creator-upload">
      <div className="gif-creator-hint-row">
        <p className="gif-creator-hint">
          Upload {MIN_GIF_IMAGES}–{MAX_GIF_IMAGES} images ({images.length}/{MAX_GIF_IMAGES} selected).
          Each image must be PNG, JPG or GIF, 2.5MB or less, and between {MIN_SOURCE_DIMENSION}px and {MAX_SOURCE_DIMENSION}px.
          Original aspect ratio is preserved; images are letterboxed to the GIF size.
        </p>
        <Tooltip
          title={`Supported formats: PNG, JPG, GIF. Max file size: 2.5MB. Dimensions: ${MIN_SOURCE_DIMENSION}–${MAX_SOURCE_DIMENSION}px. Maximum ${MAX_GIF_IMAGES} images. Images are not stretched.`}
        >
          <QuestionCircleOutlined />
        </Tooltip>
      </div>

      <Upload.Dragger
        multiple
        showUploadList={false}
        accept={ALLOWED_GIF_ACCEPT}
        disabled={remainingSlots <= 0}
        beforeUpload={(file, fileList) => {
          if (file === fileList[0]) {
            handleFiles(fileList);
          }
          return false;
        }}
        openFileDialogOnClick={remainingSlots > 0}
      >
        <p className="ant-upload-drag-icon">
          <PlusOutlined />
        </p>
        <p className="ant-upload-text">Click or drag images to this area</p>
        <p className="ant-upload-hint">
          {remainingSlots > 0
            ? `You can add ${remainingSlots} more image${remainingSlots === 1 ? "" : "s"}.`
            : `Maximum of ${MAX_GIF_IMAGES} images reached.`}
        </p>
      </Upload.Dragger>

      {images.length > 0 && (
        <Image.PreviewGroup>
          <div className="gif-creator-thumbs">
            {images.map((img, index) => (
              <div className="gif-creator-thumb" key={img.uid}>
                <Image
                  src={img.url}
                  alt={img.name || `Frame ${index + 1}`}
                  className="gif-creator-thumb-img"
                />
                <div className="gif-creator-thumb-actions">
                  <Button
                    type="text"
                    size="small"
                    icon={<LeftOutlined />}
                    disabled={index === 0}
                    onClick={() => moveImage(index, index - 1)}
                    aria-label="Move image left"
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<RightOutlined />}
                    disabled={index === images.length - 1}
                    onClick={() => moveImage(index, index + 1)}
                    aria-label="Move image right"
                  />
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemove(img.uid)}
                    aria-label="Remove image"
                  />
                </div>
              </div>
            ))}
          </div>
        </Image.PreviewGroup>
      )}
    </div>
  );
};

export default GifImageUploader;
